import React from 'react';
import { cn } from '@/utils/cn';
import { useRouter } from 'next/navigation';
import { getImageUrl } from '@/utils/string';
import { useDispatch, useSelector } from 'react-redux';
import Link from 'next/link';
import { getProductBrandUrl, getProductCategoryUrl } from '@/url/product';
import { resetProductFilters } from '@/redux/reducers/products-reducer';
import { imageTypes } from '@/constants/types';
import { closeAllMenu } from '@/redux/reducers/hamburger-menu-reducer';
import Icon from '../Icon';
import Text from '../Text';
import SubMenuItemCard from './SubMenuItemCard';
import ImageComponentV2 from '../Image/ImageComponentV2';

function SubMenuCard({
  type,
  subMenu,
  onToggleSubMenuItems,
  activeSubMenuId,
  isTopBrand = false,
}) {
  const router = useRouter();
  const dispatch = useDispatch();
  const { lang } = useSelector((state) => state.globalSettings);

  const gotoSubmenu = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (type === 'brands') {
      e.stopPropagation();

      dispatch(resetProductFilters());
      router.push(getProductBrandUrl({ slug: subMenu?.slug }));
      setTimeout(() => {
        dispatch(closeAllMenu());
      }, 1000);
      return;
    }

    if (type === 'my-account' || type === 'my-orders') {
      router.push(subMenu?.url);
      onToggleSubMenuItems(subMenu);
      setTimeout(() => {
        dispatch(closeAllMenu());
      }, 1000);
    } else return null;
  };

  if (
    type === 'product-categories' ||
    type === 'my-account' ||
    type === 'my-orders'
  ) {
    return (
      <section
        className={cn(
          'sub-menu-card flex flex-col gap-[10px] items-center px-16 py-10 transition-all duration-100 ease-linear hover:bg-heart-of-ice hover:rounded-xl',
          {
            'bg-heart-of-ice rounded-xl': activeSubMenuId === subMenu.id,
          },
        )}
      >
        {type === 'product-categories' ? (
          <div className="w-full flex gap-2 items-center hover:cursor-pointer">
            {subMenu?.icon ? (
              <ImageComponentV2
                src={subMenu?.icon ? getImageUrl(subMenu?.icon) : null}
                alt={subMenu?.name?.[lang]}
                className="max-w-[24px] w-full h-[24px] object-contain object-center"
                type={imageTypes.icon}
                width={24}
                height={24}
                loading="lazy"
              />
            ) : null}

            <Link
              prefetch={false}
              aria-label="Go to product category page"
              href={getProductCategoryUrl({ slug: subMenu?.slug })}
              passHref
            >
              <Text
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  dispatch(resetProductFilters());
                  router.push(getProductCategoryUrl({ slug: subMenu?.slug }));
                  setTimeout(() => {
                    dispatch(closeAllMenu());
                  }, 800);
                }}
                type="paragraph"
                className="text-14 text-coarse-wool font-medium font-encode-sans leading-20 tracking-[0.28px]"
              >
                {subMenu?.name?.en}
              </Text>
            </Link>

            {subMenu?.children?.length > 0 ? (
              <Icon
                type="thin-right-arrow"
                width="20"
                height="20"
                className={cn('size-[14px] ml-auto rotate-90', {
                  '!-rotate-90': activeSubMenuId === subMenu.id,
                })}
                color="#49525E"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleSubMenuItems(subMenu);
                }}
                role="presentation"
              />
            ) : null}
          </div>
        ) : (
          <div
            className="w-full flex gap-2 items-center hover:cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              gotoSubmenu(e);
            }}
            role="presentation"
          >
            {subMenu?.icon && (
              <Icon
                type={subMenu?.icon}
                width="24px"
                height="24px"
                color="#49525E"
              />
            )}

            <Text
              type="paragraph"
              className="text-14 text-coarse-wool font-medium font-encode-sans leading-20 tracking-[0.28px]"
            >
              {subMenu.label}
            </Text>
            <Icon
              type="thin-right-arrow"
              width="20"
              height="20"
              className="ml-auto"
              color="#49525E"
            />

            {subMenu?.children?.length > 0 ? (
              <Icon
                type="thin-right-arrow"
                width="20"
                height="20"
                className={cn('size-[14px] ml-auto rotate-90', {
                  '!-rotate-90': activeSubMenuId === subMenu.id,
                })}
                color="#49525E"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleSubMenuItems(subMenu);
                }}
                role="presentation"
              />
            ) : null}
          </div>
        )}

        {activeSubMenuId === subMenu.id && subMenu?.children?.length > 0 ? (
          <SubMenuItemCard
            data={subMenu}
            activeSubMenuId={activeSubMenuId}
            type={type}
          />
        ) : null}
      </section>
    );
  }
  if (type === 'brands') {
    return (
      <Link
        prefetch={false}
        aria-label="Go to product brand page"
        href={getProductBrandUrl({ slug: subMenu?.slug })}
        passHref
      >
        <div role="presentation" onClick={(e) => gotoSubmenu(e)}>
          {isTopBrand ? (
            <div className="w-full mb-20 p-16 rounded-xl shadow-what-are-you-looking-for-box-shadow cursor-pointer bg-pale-grey text-black">
              <ImageComponentV2
                src={
                  subMenu?.navbar_icon
                    ? getImageUrl(subMenu?.navbar_icon)
                    : null
                }
                alt={subMenu?.name[lang]}
                className="max-w-[200px] w-full h-[58px] object-contain object-center mx-auto"
                type={imageTypes.wide}
                width={200}
                height={58}
                loading="lazy"
              />
            </div>
          ) : (
            <Text
              key={subMenu?.name?.[lang]}
              type="paragraph"
              className="text-16 text-coarse-wool font-semibold font-encode-sans leading-20 tracking-[0.32px] hover:cursor-pointer mb-[16px] ml-[32px]"
            >
              {subMenu?.name?.[lang]}
            </Text>
          )}
        </div>
      </Link>
    );
  }
}

export default SubMenuCard;
